import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { PERMISSIONS } from '../../../utils/permissions';
import {
  getGuardrailsEnvironments,
  getComplianceStats,
  getGuardrailsResources,
  getGuardrailsViolations,
  getGuardrailsScans,
  triggerEnvironmentScan,
} from '../../../services/cloudopsApi';
import { FiShield as Shield, FiPlay as Play, FiArrowLeft as ArrowLeft } from 'react-icons/fi';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';

interface EnvDetailEnvironment {
  id: string;
  name?: string;
  aws_account_id: string;
  environment_type?: string;
  role_name?: string;
  description?: string;
}

interface EnvDetailResource {
  id: string;
  resource_type: string;
  resource_id: string;
  region: string;
  last_seen_at: string;
  [key: string]: unknown;
}

interface EnvDetailViolation {
  id: string;
  message: string;
  severity: string;
  category: string;
  status: string;
  detected_at: string;
}

interface EnvDetailScan {
  id: string;
  scan_type: string;
  status: string;
  started_at: string;
  completed_at?: string;
  resources_discovered?: number;
  violations_found?: number;
}

interface EnvComplianceStats {
  compliance_score: number;
  total_resources: number;
  total_violations: number;
  last_scan_at?: string;
  violations_by_severity?: Record<string, number>;
  violations_by_category?: Record<string, number>;
}

const EnvironmentDetailsPage: React.FC = () => {
  const { environmentId } = useParams<{ environmentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedClientId, isLoadingClients } = useClient();
  const { hasPermission } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<EnvDetailEnvironment | null>(null);
  const [complianceStats, setComplianceStats] = useState<EnvComplianceStats | null>(null);
  const [resources, setResources] = useState<EnvDetailResource[]>([]);
  const [violations, setViolations] = useState<EnvDetailViolation[]>([]);
  const [scans, setScans] = useState<EnvDetailScan[]>([]);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [resourceTypeFilter, setResourceTypeFilter] = useState<string>('all');
  const [scanning, setScanning] = useState<boolean>(false);

  const loadEnvironmentData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Avoid calling /guardrails/environments without client_id for partner/aws users.
      const needsClientId = isAWSUser() ||
        hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
        hasPermission(PERMISSIONS.CLIENTS_MANAGE) ||
        hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
        hasPermission(PERMISSIONS.PARTNERS_MANAGE);
      if (needsClientId && !selectedClientId) {
        if (isLoadingClients) return;
        throw new Error('Please select a client to view this environment.');
      }
      const clientId = selectedClientId || user?.client_id || undefined;

      // Get environment from list (since there's no single environment endpoint)
      const environments = await getGuardrailsEnvironments(clientId);
      const environmentData = environments.find((e: EnvDetailEnvironment) => e.id === environmentId);
      
      if (!environmentData) {
        throw new Error('Environment not found');
      }

      setEnvironment(environmentData);

      // Load related data (resources: first page, limit 50 for overview)
      const envId = environmentId!;
      const [stats, resourcesResponse, violationsData, scansData] = await Promise.all([
        getComplianceStats(envId).catch(() => null),
        getGuardrailsResources(envId, resourceTypeFilter === 'all' ? undefined : resourceTypeFilter, 1, 50).catch(() => ({ data: [] })),
        getGuardrailsViolations(envId).catch(() => []),
        getGuardrailsScans(envId).catch(() => []),
      ]);

      setComplianceStats(stats);
      const resourcesList = resourcesResponse?.data ?? (Array.isArray(resourcesResponse) ? resourcesResponse : []);
      setResources(resourcesList);
      setViolations(violationsData || []);
      setScans(scansData || []);
    } catch (err: unknown) {
      console.error('Error loading environment data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load environment details');
    } finally {
      setLoading(false);
    }
  }, [environmentId, resourceTypeFilter, selectedClientId, isLoadingClients, hasPermission, isAWSUser, user?.client_id]);

  useEffect(() => {
    if (environmentId) {
      loadEnvironmentData();
    }
  }, [environmentId, loadEnvironmentData]);

  const handleScan = async () => {
    setScanning(true);
    try {
      await triggerEnvironmentScan(environmentId!, 'full');
      toast.success('Scan triggered successfully');
      setActiveTab('scans');
    } catch (err: unknown) {
      console.error('Error triggering scan:', err);
      toast.error('Failed to trigger scan: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setScanning(false);
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getComplianceScoreColor = (score: number | null | undefined): string => {
    if (score == null) {
      return 'text-gray-500 dark:text-gray-400';
    }
    if (score >= 95) {
      return 'text-green-600 dark:text-green-400';
    }
    if (score >= 80) {
      return 'text-yellow-600 dark:text-yellow-400';
    }
    return 'text-red-600 dark:text-red-400';
  };

  const hasComplianceData =
    complianceStats &&
    complianceStats.total_resources > 0 &&
    (complianceStats.last_scan_at || complianceStats.total_violations >= 0);
  const complianceScoreValue = hasComplianceData ? complianceStats.compliance_score : null;

  const getScanStatusColor = (status: string | undefined): string => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'in_progress':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !environment) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error || 'Environment not found'}</p>
          <Button
            onClick={() => navigate('/cloudops/guardrails/environments')}
            variant="ghost"
            size="sm"
            className="mt-2"
          >
            Back to Environments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/cloudops/guardrails/environments')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Environments
        </Button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {environment.name || environment.aws_account_id}
              </h1>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                {environment.aws_account_id}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={handleScan}
              disabled={scanning}
              className="flex items-center gap-2"
            >
              <Play className="w-4 h-4" />
              {scanning ? 'Scanning...' : 'Trigger Scan'}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-8">
          {['overview', 'resources', 'violations', 'scans', 'compliance'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {complianceStats && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Compliance Score
                      </p>
                      <p
                        className={`text-2xl font-bold mt-2 ${getComplianceScoreColor(complianceScoreValue)}`}
                      >
                        {complianceScoreValue === null ? 'No scans yet' : `${complianceScoreValue}%`}
                      </p>
                    </div>
                    <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  {complianceScoreValue === null && (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Trigger a scan to generate compliance metrics for this environment.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Environment Information</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{environment.name || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">AWS Account ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{environment.aws_account_id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Environment Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{environment.environment_type || 'aws'}</dd>
                </div>
                {environment.role_name && (
                  <div>
                    <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Role Name</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{environment.role_name}</dd>
                  </div>
                )}
                {environment.description && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{environment.description}</dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Resources Tab */}
      {activeTab === 'resources' && (
        <div>
          <div className="mb-4">
            <Select
              label="Filter by Resource Type"
              value={resourceTypeFilter}
              onChange={(e) => setResourceTypeFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'ec2', label: 'EC2' },
                { value: 's3', label: 'S3' },
                { value: 'rds', label: 'RDS' },
                { value: 'lambda', label: 'Lambda' },
                { value: 'iam', label: 'IAM' },
                { value: 'security_group', label: 'Security Group' },
                { value: 'vpc', label: 'VPC' },
                { value: 'cloudtrail', label: 'CloudTrail' },
              ]}
            />
          </div>
          <Card>
            <CardContent className="p-6">
              {resources.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Resource ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Region</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Seen</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {resources.map((resource) => (
                        <tr key={resource.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{resource.resource_type}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{resource.resource_id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{resource.region}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatDate(resource.last_seen_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-600 dark:text-gray-400 py-8">No resources found</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Violations Tab */}
      {activeTab === 'violations' && (
        <div>
          <Card>
            <CardContent className="p-6">
              {violations.length > 0 ? (
                <div className="space-y-4">
                  {violations.map((violation) => (
                    <div
                      key={violation.id}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => navigate(`/cloudops/guardrails/violations/${violation.id}`)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/cloudops/guardrails/violations/${violation.id}`); }} role="button" tabIndex={0}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{violation.message}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {violation.severity} • {violation.category} • Detected: {formatDate(violation.detected_at)}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          violation.status === 'open'
                            ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                            : 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                        }`}>
                          {violation.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-600 dark:text-gray-400 py-8">No violations found</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Scans Tab */}
      {activeTab === 'scans' && (
        <div>
          <Card>
            <CardContent className="p-6">
              {scans.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Started</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completed</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Resources</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Violations</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {scans.map((scan) => (
                        <tr
                          key={scan.id}
                          onClick={() => navigate(`/cloudops/guardrails/scans/${scan.id}`)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/cloudops/guardrails/scans/${scan.id}`); }} role="row" tabIndex={0}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{scan.scan_type}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getScanStatusColor(scan.status)}`}>
                              {scan.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatDate(scan.started_at)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatDate(scan.completed_at)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{scan.resources_discovered || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{scan.violations_found || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-600 dark:text-gray-400 py-8">No scans found</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && complianceStats && (
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Compliance Score</p>
                <p className={`text-5xl font-bold ${
                  complianceStats.compliance_score >= 95
                    ? 'text-green-600 dark:text-green-400'
                    : complianceStats.compliance_score >= 80
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {complianceStats.compliance_score || 0}%
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Violations by Severity</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-2">
                  {Object.entries(complianceStats.violations_by_severity || {}).map(([severity, count]) => (
                    <div key={severity} className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-gray-400 capitalize">{severity}</dt>
                      <dd className="text-sm font-semibold text-gray-900 dark:text-white">{count}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Violations by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-2">
                  {Object.entries(complianceStats.violations_by_category || {}).map(([category, count]) => (
                    <div key={category} className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-gray-400 capitalize">{category}</dt>
                      <dd className="text-sm font-semibold text-gray-900 dark:text-white">{count}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentDetailsPage;

